import { Box, Typography } from '@mui/material';
import { useGridApiRef } from '@mui/x-data-grid';
import type { ReportJobTypes } from '@weft/types';
import { REPORT_JOB_TYPES } from '@weft/types';
import { isNotNullOrUndefined, isNullOrUndefined, raiseUnreachable, withDefault } from '@weft/utils';
import { useGetCustomerRevenueReport } from 'apps/app/src/hooks/requests/use-get-customer-revenue-report';
import { useSearchWithSearchParams } from 'apps/app/src/hooks/use-search-with-search-params';
import { saveExcel } from 'apps/app/src/utils/save-excel';
import { runMutation } from 'apps/app/src/utils/vy-mutation';
import ExcelJs from 'exceljs';
import { VyTableToolbar } from '../../../../components/new-design/table/vy-table-toolbar/vy-table-toolbar';
import { useArAgingReport } from '../../../../hooks/requests/use-ar-aging-report';
import { toastFromPromise } from '../../../../utils/vy-toasts';
import { CSV_CUSTOMER_REVENUE_FILENAME, REPORTS_WITH_DISABLED_ACTIONS } from '../reports-page-consts/report-page-content-consts';
import { useReportTypeStateParam } from '../reports-page-hooks/report-type-state-param-hook';
import type { ReportType } from '../reports-page-types/report-type';
import { REPORT_TYPES } from '../reports-page-types/report-type';
import { createInvoicesWorksheet } from '../reports-page-utils/customer-revenue-report-utils/worksheet-creators/create-invoices-worksheet';
import { createRevenueReportWorksheet } from '../reports-page-utils/customer-revenue-report-utils/worksheet-creators/create-revenue-report-worksheet';
import { createSummaryWorksheet } from '../reports-page-utils/customer-revenue-report-utils/worksheet-creators/create-summary-worksheet';
import { AllReports } from './all-reports/AllReports';
import { ArAgingReportSection } from './ar-aging-report-section/ArAgingReportSection';
import { ReportJobsSection } from './report-jobs-section/ReportJobsSection';
import { ReportMenu } from './ReportMenu';

export const ReportsPageContent: React.FC = () => {
  const customerRevenueReportMutation = useGetCustomerRevenueReport();
  const [searchText, setSearchText] = useSearchWithSearchParams();
  const [reportType] = useReportTypeStateParam();
  const { data: arAgingReport } = useArAgingReport();
  const arAgingReportApiRef = useGridApiRef();
  const selectedReportJob = getReportJobType(reportType ?? REPORT_TYPES.COMMITMENT_USAGE);
  const isViewingAllReports = reportType === REPORT_TYPES.ALL_REPORTS;

  const handleExportToCsv = async () => {
    if (isNullOrUndefined(arAgingReport)) {
      return;
    }

    await toastFromPromise((async () => {
      const workbook = new ExcelJs.Workbook();

      createInvoicesWorksheet(workbook, arAgingReport);
      createSummaryWorksheet(workbook, arAgingReport);

      await saveExcel(workbook, 'AR Aging Report');
    })(), {
      pending: 'Downloading...',
      success: 'Download complete',
      error: 'Failed to download',
    });
  };

  const customerRevenueDownloadProps = selectedReportJob !== REPORT_JOB_TYPES.CustomerRevenue && !isViewingAllReports ? {} : {
    asDownload: true,
    onDownload: async (reportJobId: string) => {
      await runMutation(
        customerRevenueReportMutation,
        { reportId: reportJobId },
        {
          pending: 'Downloading...',
          success: 'Download complete',
          error: 'Failed to download',
        },
        {
          onSuccess: async (customerRevenueReport) => {
            const workbook = new ExcelJs.Workbook();
            createRevenueReportWorksheet(workbook, customerRevenueReport);

            await saveExcel(workbook, CSV_CUSTOMER_REVENUE_FILENAME);
          },
        },
      );
    },
  };

  return (
    <>
      <VyTableToolbar>
        <Box sx={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
          <Typography
            variant="h6"
            fontWeight={500}
            fontSize="20px"
          >
            {reportType}
          </Typography>
        </Box>
        {reportType && !REPORTS_WITH_DISABLED_ACTIONS.includes(reportType) && (
          <VyTableToolbar.Actions>
            <VyTableToolbar.SearchInput
              onChange={(event) => setSearchText(event.target.value)}
              defaultValue={withDefault(searchText, '')}
              placeholder='Search name...'
              showLabel={false}
            />
            <ReportMenu
              selectedReportJob={selectedReportJob}
              reportType={reportType}
              onExportCsv={handleExportToCsv}
              shouldExportAsCsv={reportType === REPORT_TYPES.AR_AGING}
              shouldRequestReport
            />
          </VyTableToolbar.Actions>
        )}
      </VyTableToolbar>

      {isNotNullOrUndefined(selectedReportJob) && (
        <ReportJobsSection
          {...customerRevenueDownloadProps}
          reportJobType={selectedReportJob}
          filter={searchText ?? undefined}
        />
      )}

      {reportType === REPORT_TYPES.AR_AGING && (
        <ArAgingReportSection
          apiRef={arAgingReportApiRef}
        />
      )}

      {isViewingAllReports && (<AllReports {...customerRevenueDownloadProps} />)}
    </>
  );
};

export function getReportJobType(reportType: ReportType): ReportJobTypes | undefined {
  switch (reportType) {
    case REPORT_TYPES.COMMITMENT_USAGE:
      return REPORT_JOB_TYPES.CommitmentUsage;
    case REPORT_TYPES.UNCHARGED_USAGE:
      return REPORT_JOB_TYPES.UnchargedUsage;
    case REPORT_TYPES.PRODUCTS_USAGE:
      return REPORT_JOB_TYPES.ProductsUsage;
    case REPORT_TYPES.CUSTOMER_REVENUE:
      return REPORT_JOB_TYPES.CustomerRevenue;
    case REPORT_TYPES.CONTRACTS:
      return REPORT_JOB_TYPES.Contracts;
    case REPORT_TYPES.ANONYMOUS_CUSTOMERS:
      return REPORT_JOB_TYPES.AnonymousCustomers;
    case REPORT_TYPES.MONTHLY_CLOUSER:
      return REPORT_JOB_TYPES.MonthlyClosure;
    case REPORT_TYPES.AR_AGING:
    case REPORT_TYPES.ALL_REPORTS:
      return undefined;
    default:
      return raiseUnreachable(reportType);
  }
}
